import { LogOut, Menu } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { useAuth } from '../auth/AuthProvider.js';
import { useBranding } from '../branding/BrandingProvider.js';
import { Button } from '../components/Button.js';

import { NAV } from './nav.js';
import { TenantSwitcher } from './TenantSwitcher.js';

/**
 * The context bar: where you are, whose account you are in, and who you are.
 *
 * It is **solid, not translucent**. A blurred bar over scrolling content is a
 * contrast pair nobody measured — the text sits on whatever happens to be
 * underneath it at that scroll position — and the sticky table header below
 * relies on this bar being an opaque object it can offset itself against.
 *
 * The height is `--topbar-h` rather than padding-plus-whatever-the-tallest-
 * control-turns-out-to-be, because `Table` reads that same token for
 * `top-[var(--topbar-h)]` on its sticky header. Two components agreeing by
 * arithmetic is two components that drift on the next edit.
 */
export function TopBar({ onOpenNav }: { onOpenNav?: () => void }): ReactElement {
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [busy, setBusy] = useState(false);

  /**
   * The eyebrow is the matching nav entry's own label, so this file introduces
   * no second copy of a screen's name that could disagree with the rail. The
   * prefix match is what keeps `/sims/sim_123` reading as "SIMs" rather than as
   * nothing at all; the sort is longest-first so a nested route matches its own
   * entry before a shorter ancestor's.
   */
  const current = [...NAV]
    .sort((a, b) => b.to.length - a.to.length)
    .find((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));

  return (
    <header className="chrome sticky top-0 z-30 flex h-[var(--topbar-h)] shrink-0 items-center justify-between gap-[var(--space-4)] border-b border-line px-[var(--space-4)] md:px-[var(--space-6)]">
      <div className="flex min-w-0 items-center gap-[var(--space-3)]">
        {/*
          Below `md` the rail is not rendered, so this is the only way to the
          navigation. Above it the button would be a second control for
          something already permanently on screen.
        */}
        <button
          type="button"
          onClick={onOpenNav}
          aria-label="Open navigation"
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-sm)] text-muted transition-colors duration-[var(--motion-fast)] hover:bg-surface hover:text-body md:hidden"
        >
          <Menu aria-hidden="true" size={18} strokeWidth={2} />
        </button>

        {/*
          The lockup lives in the rail's brand block now. It is repeated here
          only where the rail is hidden, so a phone still says whose panel this
          is — and the fallback is byte-identical to the rail's, because "no
          logo configured" must not mean "no identity".
        */}
        <div className="flex min-w-0 items-center md:hidden">
          {branding?.logoUrl ? (
            // The alt text is the product name, not "logo": a screen reader
            // announcing "logo" tells the user nothing about whose panel this is.
            <img
              src={branding.logoUrl}
              alt={branding.productName}
              className="h-6 w-auto max-w-full object-contain"
            />
          ) : (
            <span className="truncate text-[length:var(--text-sm)] font-semibold tracking-[var(--tracking-tight)] text-body">
              {branding?.productName ?? 'Connect Panel'}
            </span>
          )}
        </div>

        {current ? (
          <p className="hidden min-w-0 items-center gap-[var(--space-2)] text-[length:var(--text-sm)] font-medium text-body md:flex">
            <current.icon aria-hidden="true" size={14} strokeWidth={2} className="shrink-0 text-muted" />
            <span className="truncate">{current.label}</span>
          </p>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-[var(--space-3)]">
        <TenantSwitcher />

        {/* Structural, not decorative: it separates "which account" from "which
            person", two things that are easy to conflate at a glance. */}
        <span aria-hidden="true" className="hidden h-4 w-px bg-line sm:block" />

        {/* `sm:block`, not `sm:inline`: `truncate` needs a block box to clip
            against, and an inline one silently ignores the max width.

            `--text-xs`, not `--text-2xs`. 11px is the size this system reserves
            for a micro-label above something — a column header, an eyebrow —
            and the signed-in identity is not a label on anything. It is the
            answer to "am I about to act as the right person", which is a
            question worth reading without leaning in. */}
        <span className="hidden max-w-[16rem] truncate text-[length:var(--text-xs)] text-muted sm:block">
          {user?.email}
        </span>

        {/*
          `size="sm"`: 28px, so the control sits *inside* the 52px bar rather
          than filling it. The glyph is `Button`'s own decorative slot, which
          renders it `aria-hidden` — the accessible name stays "Sign out".
        */}
        <Button
          variant="ghost"
          size="sm"
          icon={LogOut}
          loading={busy}
          onClick={() => {
            setBusy(true);
            void logout().finally(() => {
              setBusy(false);
              void navigate('/login', { replace: true });
            });
          }}
        >
          Sign out
        </Button>
      </div>
    </header>
  );
}
